import { buildApiUrl } from '../../services/apiConfig';

class DocIntelligenceService {
  async request(endpoint, options = {}) {
    const token = localStorage.getItem('auth_token') || 'dev-token';
    const response = await fetch(buildApiUrl('NOVA_INGESTION', endpoint), {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    });
    if (!response.ok) {
      throw new Error(`API error ${response.status}: ${await response.text()}`);
    }
    return response.json();
  }

  // Folder operations
  async listFolders() {
    return this.request('/docugraph/scan/folders');
  }

  async scanFolder(folderPath) {
    return this.request('/docugraph/scan', {
      method: 'POST',
      body: JSON.stringify({ folder_path: folderPath }),
    });
  }

  async browseFolders(path) {
    const params = path ? `?path=${encodeURIComponent(path)}` : '';
    return this.request(`/docugraph/scan/browse${params}`);
  }

  async previewFolder(path) {
    return this.request(`/docugraph/scan/preview?path=${encodeURIComponent(path)}`);
  }

  async uploadFiles(files) {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    const token = localStorage.getItem('auth_token') || 'dev-token';
    const response = await fetch(buildApiUrl('NOVA_INGESTION', '/docugraph/scan/upload'), {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) throw new Error(`Upload error ${response.status}`);
    return response.json();
  }

  // Document operations
  async listDocuments(params) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request(`/docugraph/documents${query}`);
  }

  async semanticSearch(query, limit = 20) {
    const params = new URLSearchParams({ query, limit: String(limit) });
    return this.request(`/docugraph/similarity/search/semantic?${params.toString()}`);
  }

  // Taxonomy operations
  async getTaxonomy() {
    return this.request('/docugraph/taxonomy');
  }

  async generateTaxonomy() {
    return this.request('/docugraph/taxonomy/generate', { method: 'POST' });
  }

  async approveAll() {
    return this.request('/docugraph/taxonomy/approve-all', { method: 'POST' });
  }

  async createNode(payload) {
    return this.request('/docugraph/taxonomy', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteNode(nodeId) {
    return this.request(`/docugraph/taxonomy/${nodeId}`, { method: 'DELETE' });
  }

  // Classification
  async classifyDocuments() {
    return this.request('/docugraph/classify', { method: 'POST' });
  }

  async getDocumentAnalysis() {
    return this.request('/docugraph/classify/analysis');
  }

  // Quality assessment
  async listAssessableFolders() {
    return this.request('/docugraph/quality/folders');
  }

  async runQualityAssessment(folderId) {
    return this.request('/docugraph/quality/run', {
      method: 'POST',
      body: JSON.stringify({ folder_id: folderId }),
    });
  }

  async listQualityRuns() {
    return this.request('/docugraph/quality/runs');
  }

  async getQualityRun(runId) {
    return this.request(`/docugraph/quality/runs/${runId}`);
  }

  async updateQuestion(qaId, question) {
    return this.request(`/docugraph/quality/questions/${qaId}`, {
      method: 'PUT',
      body: JSON.stringify({ question }),
    });
  }

  // Q&A
  async askQuestion(question) {
    return this.request('/docugraph/quality/ask', {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
  }

  async getQuestionHistory(limit = 30) {
    return this.request(`/docugraph/quality/history?limit=${limit}`);
  }

  // Sources
  async listSources(type, status) {
    const params = new URLSearchParams();
    if (type)   params.set('type', type);
    if (status) params.set('status', status);
    const qs = params.toString();
    return this.request(`/sources/${qs ? '?' + qs : ''}`);
  }

  // Domain analysis
  async getCachedDomainAnalysis(sourceId, objectPaths = []) {
    const params = new URLSearchParams({ object_paths: JSON.stringify(objectPaths) });
    return this.request(`/sources/${sourceId}/domain-analysis?${params}`);
  }

  async runDomainAnalysis(sourceId, body) {
    return this.request(`/sources/${sourceId}/domain-analysis`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getDomainAnalysisRuns(sourceId, limit = 20) {
    return this.request(`/sources/${sourceId}/domain-analysis/runs?limit=${limit}`);
  }

  async getDomainAnalysisRun(sourceId, runId) {
    return this.request(`/sources/${sourceId}/domain-analysis/run/${runId}`);
  }

  // Similarity
  async getSimilarityMatrix() {
    return this.request('/docugraph/similarity/matrix');
  }

  // Duplicates
  async detectDuplicates(folderId, threshold = 0.85) {
    const params = new URLSearchParams({ threshold: String(threshold) });
    if (folderId) params.set('folder_id', String(folderId));
    return this.request(`/docugraph/similarity/duplicates?${params.toString()}`);
  }

  async saveDuplicates(folderId, threshold = 0.85) {
    const params = new URLSearchParams({ threshold: String(threshold) });
    if (folderId) params.set('folder_id', String(folderId));
    return this.request(`/docugraph/similarity/duplicates/save?${params.toString()}`, { method: 'POST' });
  }

  async getDuplicateClusters(folderId, status) {
    const params = new URLSearchParams();
    if (folderId) params.set('folder_id', String(folderId));
    if (status) params.set('status', status);
    const qs = params.toString();
    return this.request(`/docugraph/similarity/duplicates/clusters${qs ? '?' + qs : ''}`);
  }

  async reviewCluster(clusterId, payload) {
    return this.request(`/docugraph/similarity/duplicates/clusters/${clusterId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async getAuditLog(folderId, limit = 50) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (folderId) params.set('folder_id', String(folderId));
    return this.request(`/docugraph/similarity/audit-log?${params.toString()}`);
  }
}

export default new DocIntelligenceService();
